import { Dialog, DialogBackdrop,  Transition, TransitionChild } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {FC, Fragment, useRef, useState, ReactNode, Dispatch, SetStateAction} from "react";
import { classNames, wrapClick } from "utils";

interface FilePreviewProps {
  label?: string;
  url: string;
  children?: ReactNode;
  trigger?: (props: { setOpen: Dispatch<SetStateAction<boolean>> }) => JSX.Element;
}

export const FilePreview: FC<FilePreviewProps> = ({ url, label, trigger: TriggerComponent }) => {
  const [open, setOpen] = useState(false);
  const cancelButtonRef = useRef(null);


  const Trigger = () =>
    TriggerComponent
      ? <TriggerComponent setOpen={setOpen} />
      : (
        <>
          <button
            onClick={wrapClick(() => setOpen(true))}
            className="font-medium text-primary-600 hover:text-primary-500"
          >
            Preview
          </button>
        </>
      )

  return (
    <>
      <Trigger />
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className={classNames("theme", "fixed z-10 inset-0 overflow-y-auto")}
          initialFocus={cancelButtonRef}
          onClose={() => setOpen(false)}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div>
                <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white rounded-md text-primary-500 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600"
                    onClick={() => {setOpen(false)}}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </TransitionChild>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="max-w-7xl inline-block align-bottom bg-white dark:bg-gray-900 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full">
                <img src={url} alt={label}/>
              </div>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default FilePreview;
